import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

/** Small building blocks for the editor panels. */

export function Section({
  title,
  children,
  actions,
  className,
}: {
  title?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("border-b border-zinc-100 px-4 py-3.5", className)}>
      {title ? (
        <div className="mb-2.5 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-zinc-700">{title}</h3>
          {actions}
        </div>
      ) : null}
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

export function Row({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("flex items-center gap-2", className)}>{children}</div>;
}

export function Tip({
  label,
  children,
  side = "bottom",
}: {
  label: ReactNode;
  children: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
}) {
  return (
    <TooltipPrimitive.Root delayDuration={400}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-[60] rounded-md bg-zinc-900 px-2 py-1 text-xs text-white shadow-lg"
        >
          {label}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export function IconButton({
  label,
  active,
  disabled,
  onClick,
  children,
  className,
  shortcut,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
  shortcut?: string;
}) {
  return (
    <Tip label={shortcut ? `${label} (${shortcut})` : label}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={active}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          "inline-flex size-8 shrink-0 items-center justify-center rounded-md text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900 disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-4",
          active && "bg-brand-50 text-brand-700 hover:bg-brand-100",
          className,
        )}
      >
        {children}
      </button>
    </Tip>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  className,
}: {
  value: T | undefined;
  options: { value: T; label: ReactNode; title: string }[];
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    <div className={cn("inline-flex rounded-lg bg-zinc-100 p-0.5", className)}>
      {options.map((option) => (
        <Tip key={option.value} label={option.title}>
          <button
            type="button"
            aria-label={option.title}
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              "inline-flex h-7 min-w-7 flex-1 items-center justify-center rounded-md px-1.5 text-xs text-zinc-600 [&_svg]:size-4",
              value === option.value && "bg-white text-zinc-900 shadow-sm",
            )}
          >
            {option.label}
          </button>
        </Tip>
      ))}
    </div>
  );
}

/**
 * Numeric field: type a value, use ↑/↓ (Shift ×10) or drag the label.
 * `mixed` shows an empty field for multi-selections with different values.
 */
export function NumberField({
  label,
  value,
  onChange,
  onCommit,
  step = 1,
  min,
  max,
  unit,
  precision = 0,
  mixed,
  className,
  disabled,
}: {
  label: ReactNode;
  value: number;
  onChange: (value: number) => void;
  /** Called once when a drag or edit ends (for undo grouping). */
  onCommit?: () => void;
  step?: number;
  min?: number;
  max?: number;
  unit?: string;
  precision?: number;
  mixed?: boolean;
  className?: string;
  disabled?: boolean;
}) {
  const format = (v: number) => (Number.isFinite(v) ? String(Number(v.toFixed(precision))) : "");
  const [text, setText] = useState(mixed ? "" : format(value));
  const [focused, setFocused] = useState(false);
  const drag = useRef<{ x: number; value: number } | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: format depends on precision only
  useEffect(() => {
    if (!focused) setText(mixed ? "" : format(value));
  }, [value, mixed, focused, precision]);
  const clamp = (v: number) =>
    Math.min(max ?? Number.POSITIVE_INFINITY, Math.max(min ?? Number.NEGATIVE_INFINITY, v));
  const commitText = () => {
    const parsed = Number(text.replace(",", "."));
    if (text.trim() !== "" && Number.isFinite(parsed)) onChange(clamp(parsed));
    else setText(mixed ? "" : format(value));
    onCommit?.();
  };
  return (
    <label
      className={cn(
        "flex h-8 min-w-0 items-center rounded-md border border-zinc-200 bg-white text-sm focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20",
        disabled && "opacity-50",
        className,
      )}
    >
      <span
        className="flex h-full shrink-0 cursor-ew-resize items-center pr-1 pl-2 text-xs text-zinc-400 select-none"
        onPointerDown={(event) => {
          if (disabled) return;
          event.preventDefault();
          (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
          drag.current = { x: event.clientX, value };
        }}
        onPointerMove={(event) => {
          if (!drag.current) return;
          const delta =
            Math.round((event.clientX - drag.current.x) / 2) * step * (event.shiftKey ? 10 : 1);
          onChange(clamp(Number((drag.current.value + delta).toFixed(Math.max(precision, 2)))));
        }}
        onPointerUp={() => {
          if (drag.current) onCommit?.();
          drag.current = null;
        }}
      >
        {label}
      </span>
      <input
        disabled={disabled}
        className="h-full w-full min-w-0 bg-transparent pr-1 text-right tabular-nums outline-none"
        value={text}
        placeholder={mixed ? "–" : undefined}
        inputMode="decimal"
        onFocus={(event) => {
          setFocused(true);
          event.currentTarget.select();
        }}
        onBlur={() => {
          setFocused(false);
          commitText();
        }}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            commitText();
            (event.currentTarget as HTMLInputElement).blur();
          } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            event.preventDefault();
            const direction = event.key === "ArrowUp" ? 1 : -1;
            const next = clamp((mixed ? 0 : value) + direction * step * (event.shiftKey ? 10 : 1));
            onChange(Number(next.toFixed(Math.max(precision, 2))));
            setText(format(next));
          } else if (event.key === "Escape") {
            setText(mixed ? "" : format(value));
            (event.currentTarget as HTMLInputElement).blur();
          }
        }}
      />
      {unit ? <span className="shrink-0 pr-2 text-xs text-zinc-400">{unit}</span> : null}
    </label>
  );
}

export function Slider({
  value,
  min,
  max,
  step = 1,
  onChange,
  onCommit,
  className,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  onCommit?: () => void;
  className?: string;
}) {
  return (
    <input
      type="range"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(event) => onChange(Number(event.target.value))}
      onPointerUp={onCommit}
      onKeyUp={onCommit}
      className={cn("h-1.5 w-full cursor-pointer accent-brand-600", className)}
    />
  );
}

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;

export function PopoverContent({
  children,
  className,
  side = "left",
  align = "start",
}: {
  children: ReactNode;
  className?: string;
  side?: "left" | "right" | "top" | "bottom";
  align?: "start" | "center" | "end";
}) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        side={side}
        align={align}
        sideOffset={8}
        collisionPadding={12}
        className={cn(
          "z-50 w-64 rounded-xl border border-zinc-200 bg-white p-3 shadow-xl outline-none",
          className,
        )}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-4 shrink-0 accent-brand-600"
      />
      <span>
        <span className="text-zinc-800">{label}</span>
        {description ? <span className="block text-xs text-zinc-500">{description}</span> : null}
      </span>
    </label>
  );
}
